import { Center, chakra, Spinner, VStack } from '@chakra-ui/react';
import React from 'react';

import useApiQuery from 'lib/api/useApiQuery';
import { Button } from 'toolkit/chakra/button';
import { DialogBody, DialogContent, DialogHeader, DialogRoot } from 'toolkit/chakra/dialog';
import { Skeleton } from 'toolkit/chakra/skeleton';
import { TableBody, TableCell, TableColumnHeader, TableHeader, TableRoot, TableRow } from 'toolkit/chakra/table';
import { Tooltip } from 'toolkit/chakra/tooltip';
import { useDisclosure } from 'toolkit/hooks/useDisclosure';
import IconSvg from 'ui/shared/IconSvg';

import { formatFactor } from './utils';

interface Props {
  className?: string;
  isLoading?: boolean;
  txHash: string;
}

const TxAiAnalysis = ({ className, isLoading, txHash }: Props) => {
  const { open, onOpen, onOpenChange } = useDisclosure();

  const txRiskAnalysisQeury = useApiQuery('ai:transaction_analysis', {
    pathParams: {
      tx_hash: txHash,
    },
    queryOptions: {
      enabled: open,
    },
  });

  const queryData = txRiskAnalysisQeury.data;

  if (isLoading) {
    return <Skeleton mx="auto" loading className={ className } w="38px" h="40px" borderRadius="base"/>;
  }

  return (
    <>
      <VStack gap={ 0 }>
        <Tooltip content="Click to view AI Risk Analysis" disableOnMobile>
          <Button variant="link" onClick={ onOpen }>
            Low
          </Button>
        </Tooltip>
        <IconSvg name="low-circle" boxSize={ 7 }/>
      </VStack>

      <DialogRoot open={ open } onOpenChange={ onOpenChange } size={{ lgDown: 'full', lg: 'sm2' }}>
        <DialogContent className="light" minH={{ lg: '520px' }}>
          <DialogHeader>AI Risk Analysis</DialogHeader>

          { !txRiskAnalysisQeury.isLoading ? (
            <DialogBody>
              <TableRoot>
                <TableHeader>
                  <TableRow>
                    <TableColumnHeader>Factor</TableColumnHeader>
                    <TableColumnHeader>Risk Level</TableColumnHeader>
                    <TableColumnHeader>Reason</TableColumnHeader>
                  </TableRow>
                </TableHeader>

                <TableBody color="text.pale" textStyle="sm">
                  { queryData?.data.factors.map((factor, i) => (
                    <TableRow key={ i }>
                      <TableCell>{ formatFactor(factor.factor) }</TableCell>
                      <TableCell>{ factor.level }</TableCell>
                      <TableCell>{ factor.reason }</TableCell>
                    </TableRow>
                  )) }

                </TableBody>
              </TableRoot>
            </DialogBody>
          ) : (
            <Center flex={ 1 }>
              <Spinner size="lg"/>
            </Center>
          ) }
        </DialogContent>
      </DialogRoot>
    </>
  );
};

export default React.memo(chakra(TxAiAnalysis));
